import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
  faChevronDown,
  faChevronUp,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import { TranslateService } from "@ngx-translate/core";
import { PageableResponse, SocietiesService, User } from "@pwc-bei/common";
import { AuthenticationService } from "@pwc-bei/security";
import { ToastrService } from "ngx-toastr";
import { first, of, switchMap } from "rxjs";
import { RoutePath } from "src/app/enums/route-path.enum";
import { ServiceAreaEnum } from "src/app/enums/service-area.enum";
import { ServiceRequestStatusEnum } from "src/app/enums/service-request-status.enum";
import { Service } from "src/app/models/coe/service";
import { ServiceRequest } from "src/app/models/coe/service-request";
import { ServiceRequestRegistry } from "src/app/models/coe/service-request-registry";
import { BasePage } from "src/app/pages/base-page/base-page";
import { ServiceRequestService } from "src/app/pages/service-requests/services/service-request.service";
import { CustomerProfileService } from "src/app/pages/services/customer-profile.service";
import { ThematicAreaServiceService } from "src/app/pages/thematic-area/services/thematic-area-service.service";
import { UserProfile } from "src/app/pages/user-profile/models/user-profile";
import { GroupEntityService } from "src/app/services/group-entity.service";
import { ServiceService } from "src/app/services/service.service";
import { ToastUtils } from "src/app/utils/toast-utils";

@Component({
    selector: "app-questionnaire-intro",
    templateUrl: "./questionnaire-intro.component.html",
    styleUrls: ["./questionnaire-intro.component.scss"],
    standalone: false
})
export class QuestionnaireIntroComponent extends BasePage implements OnInit {
  collapsed: boolean = false;

  me?: UserProfile;
  societyId: number;

  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faCircleNotch = faCircleNotch;

  currentService?: Service;
  currentServiceRequestId?: String;
  redirecting: boolean = false;

  canCreateReq: boolean = false;

  disabledForRoles = [
    "ACCESSO_B2C_OPERATORE_BANCARIO",
    "ACCESSO_DIREZIONE_GENERALE",
  ];

  emptyServiceRequestRegistry: ServiceRequestRegistry = {
    id: null,
    userId: null,
    code: null,
    businessName: null,
    cf: null,
    vatNumber: null,
    beneficiaryType: null,
    companyType: null,
    beneficiaryTrait: null,
    financialStatementNumber: null,
    belongsToGroup: null,
    hasCorporateBonds: null,
    atecoCodes: [],
    regionLegal: null,
    provinceLegal: null,
    regionOperative: null,
    provinceOperative: null,
    regionsOperative: [],
    provincesOperative: [],
    industry: null,
    lastUpdate: new Date(),
    fullnameUserB2c: null,
    customerId: null,
    vcfClientType: null,
    b2CEmail: null,
    ccMail: null,
    businessEmail: null,
    businessPec: null,
    sdi: null,
    fiscalCode: null,
    addressLegal: null,
    cityLegal: null,
    phone: null,
    lastFinancialStatementDocument: null,
    companyRegistrationDocument: null,
  };

  constructor(
    private router: Router,
    private serviceSrv: ServiceService,
    private societyService: SocietiesService,
    private customerProfileService: CustomerProfileService,
    private thematicService: ThematicAreaServiceService,
    private toast: ToastrService,
    private serviceRequestSrv: ServiceRequestService,
    private groupEntityService: GroupEntityService,
    private authService: AuthenticationService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.societyService.currentSociety$
      .pipe(
        switchMap((soc) => {
          if (soc == null || soc == undefined) {
            return this.societyService.getSelectedSociety();
          }
          return of(soc);
        }),
      )
      .subscribe((society) => {
        if (society != null && society != undefined) {
          this.societyId = society.id;
        }
      });
    this.setCanCreateReq();
  }

  setCanCreateReq() {
    this.customerProfileService
      .getMyProfile()
      .pipe(first())
      .subscribe((profile: UserProfile) => {
        this.me = profile;
        // CHECK AUTHORITIES
        this.groupEntityService
          .findByUserId(this.me.userId, null)
          .subscribe((groupEntityList) => {
            let disable = false;
            groupEntityList.forEach((groupEntity) => {
              if (this.disabledForRoles.includes(groupEntity.groupCode)) {
                disable = true;
              }
            });
            if (disable) {
              //this.ngxLoader.stop();
              this.canCreateReq = false;
            } else {
              this.canCreateReq = true;
            }
          });
      });
  }

  goToOrientationQuestionnaire() {
    this.redirecting = true;
    const search: Partial<Service> = {};
    search.area = ServiceAreaEnum.ORIENTATION;

    this.serviceSrv.findAll(search)
    .subscribe((service: PageableResponse<Service[]>) => {

      if(service && service[0]){

        this.currentService=service[0]

        this.customerProfileService.getMyProfile()
        .pipe(first())
        .subscribe((profile: UserProfile) => {
          this.serviceRequestSrv.getOpenServiceRequestForCustomerAndServiceId(profile.id, this.currentService.id, this.societyId)
          .subscribe((serviceRequestId: string) => {

              if(!serviceRequestId ){
                if("true" === sessionStorage.getItem("openingOrientationQuestionnaire")) {
                  this.toast.warning(this.translateService.instant("questionnaire.error.already-opening"), ToastUtils.TITLE_WARNING);
                  this.redirecting = false;
                  return;
                }
                sessionStorage.setItem("openingOrientationQuestionnaire", "true");
                this.sendRequest();
              } else {
                sessionStorage.removeItem("openingOrientationQuestionnaire");
                this.currentServiceRequestId = serviceRequestId;
                this.router.navigate([
                  RoutePath.SERVICE_REQUESTS_LIST + '/' + this.currentServiceRequestId
                ]);
                this.redirecting = true
              }

          });
        });
      } else {
        console.log("Orientation Service not found")
        this.router.navigate([
          RoutePath.HOME
        ]);
        this.redirecting = false
      }
    });

  }

  sendRequest() {
    if (this.currentService) {
      const serviceRequest: ServiceRequest = {
        id: null,
        code: null,
        status: ServiceRequestStatusEnum.REQUEST_CREATED,
        service: this.currentService.id,
        serviceCode: this.currentService.code,
        serviceTitle: this.currentService.title,
        serviceSummary: this.currentService.summary,
        area: this.currentService.area,
        isVcf: this.currentService.isVcf,
        createdDate: new Date(),
        societyId: this.societyId,
        b2cUserId: null,
        b2cUser: null,
        customerId: null,
        selected: null,
        createdDateFrom: null,
        createdDateTo: null,
        dossierId: null,
        serviceRequestRegistry: this.emptyServiceRequestRegistry,
        contractModel: this.currentService.contractModel,
        hasSurvey: this.currentService.hasSurvey,
        surveyRegistryVisible: this.currentService.surveyRegistryVisible,
        b2cSurveyAvailability: this.currentService.b2cSurveyAvailability,
        reportEnabled: this.currentService.reportEnabled,
        automaticReportGeneration:
          this.currentService.automaticReportGeneration,
        reportSections: this.currentService.reportSections,
      };
      this.customerProfileService.getMyProfile().pipe(
        switchMap(profile => {
          if(profile) {
            serviceRequest.customerId = profile.id;
            serviceRequest.serviceRequestRegistry.industry = profile.industry;
            serviceRequest.serviceRequestRegistry.businessName = profile.businessName;
          }
          return this.authService.dataUser()
        }),
        switchMap(user => {
          if(user) {
            serviceRequest.b2cUserId = user.id;
            serviceRequest.b2cUser = user.username;
            serviceRequest.serviceRequestRegistry.fullnameUserB2c =
              user.firstName + ' ' + user.lastName;
            serviceRequest.serviceRequestRegistry.phone = user.phone;
            serviceRequest.serviceRequestRegistry.userId = user.id;
            serviceRequest.serviceRequestRegistry.b2CEmail = user.email;
          }
          return this.thematicService.insertRequest(serviceRequest)
        })
      ).subscribe({
        next: (res) => {
          this.toast.success(
            this.translateService.instant("toast.success.operation-success"),
            ToastUtils.TITLE_SUCCESS
          );
          this.currentServiceRequestId = res;
          sessionStorage.removeItem("openingOrientationQuestionnaire");
          this.router.navigate([
            RoutePath.SERVICE_REQUESTS_LIST + '/' + this.currentServiceRequestId
          ]);
          this.redirecting = true
        }, error: (error) => {
          sessionStorage.removeItem("openingOrientationQuestionnaire");
          if (error.error && error.error.type === 'service_operation_exception')
            this.toast.error(error.error.details.message, ToastUtils.TITLE_ERROR);
          else
              this.toast.error(
                this.translateService.instant("toast.error.save-error"),
                ToastUtils.TITLE_ERROR,
              );
        }
      });
    }
  }
}
